import { APIAttachment, ResourceType } from '@ardoq/api-types';
import {
  ObservableState,
  persistentReducedStream,
  reducer,
  streamReducer,
  toPersistentStream,
} from '@ardoq/rxbeach';
import { DocumentArchiveContext } from '@ardoq/api-types';
import { combineLatest, map } from 'rxjs';
import { filterResourceType, websocket$ } from 'sync/websocket$';
import { websocketReducer } from '../crud/reducers';
import {
  getStreamCollectionInitialState,
  toStreamCollection,
} from '../utils/streamUtils';
import {
  resetDocumentArchiveAttachments,
  setOrgDocumentArchiveAttachments,
  setWorkspaceDocumentArchiveAttachments,
} from './actions';
import { fixDataOperations, toAttachmentContext } from './fixDataOperations';
import {
  removeEntityById,
  returnPayload,
  updateEntity,
} from '@ardoq/common-helpers';
import {
  AttachmentWithContext,
  DocumentArchiveAttachmentsState,
} from './types';
import { localeCompare } from '@ardoq/locale';
import { locale$ } from '../locale$';

export const isWorkspaceAttachment = (attachment: APIAttachment): boolean =>
  toAttachmentContext(attachment) === DocumentArchiveContext.WS;

const handleSetOrgAttachments = (
  state: APIAttachment[],
  orgAttachments: APIAttachment[]
): APIAttachment[] => {
  const workspaceAttachments = state.filter(isWorkspaceAttachment);

  return [...workspaceAttachments, ...orgAttachments];
};

const handleSetWorkspaceAttachments = (
  state: APIAttachment[],
  attachments: APIAttachment[]
): APIAttachment[] => {
  const orgAttachments = state.filter(
    attachment => !isWorkspaceAttachment(attachment)
  );

  return [...orgAttachments, ...attachments];
};

const handleAttachmentCreated = (
  state: APIAttachment[],
  attachment: APIAttachment
): APIAttachment[] => [...state, attachment];

const _documentArchiveAttachments$ = persistentReducedStream<APIAttachment[]>(
  '_documentArchiveAttachments$',
  [],
  [
    reducer(setOrgDocumentArchiveAttachments, handleSetOrgAttachments),
    reducer(
      setWorkspaceDocumentArchiveAttachments,
      handleSetWorkspaceAttachments
    ),
    reducer(resetDocumentArchiveAttachments, returnPayload),
    streamReducer(
      websocket$.pipe(
        filterResourceType<APIAttachment>(
          ResourceType.DOCUMENT_ARCHIVE_ATTACHMENT
        )
      ),
      websocketReducer({
        create: handleAttachmentCreated,
        update: updateEntity,
        delete: removeEntityById,
      })
    ),
  ]
);

const addContextToAttachments = (
  attachments: APIAttachment[]
): AttachmentWithContext[] =>
  attachments.map(attachment =>
    fixDataOperations.addContextToAttachment(attachment)
  );

const sortedDocumentArchiveAttachments$ = combineLatest([
  _documentArchiveAttachments$,
  locale$,
]).pipe(
  map(([attachments, locale]) =>
    attachments.toSorted((a, b) =>
      localeCompare(a.filename, b.filename, locale)
    )
  )
);

const documentArchiveAttachments$: ObservableState<DocumentArchiveAttachmentsState> =
  toPersistentStream(
    'documentArchiveAttachments$',
    sortedDocumentArchiveAttachments$.pipe(
      map(addContextToAttachments),
      map(toStreamCollection)
    ),
    getStreamCollectionInitialState()
  );

export { documentArchiveAttachments$ };
